import React, { useState } from 'react';
import './Calculator.css';

export default function Calculator() {
  const [peptideAmount, setPeptideAmount] = useState(5);   // mg
  const [waterAmount, setWaterAmount]     = useState(2);   // mL
  const [desiredDose, setDesiredDose]     = useState(250); // mcg

  const concentration = (peptideAmount * 1000) / waterAmount; // mcg/mL
  const volumePerDose = desiredDose / concentration;           // mL
  const dosesPerVial  = waterAmount / volumePerDose;

  return (
    <div>
      <div className="page-hero">
        <div className="container">
          <h1>Peptide Reconstitution Calculator</h1>
          <p>Accurately calculate dosing volumes for your research experiments</p>
        </div>
      </div>
      <div className="container calc-page section">
        <div className="calc-layout">
          {/* Inputs */}
          <div className="calc-form card">
            <h2>Research Parameters</h2>
            <div className="calc-field">
              <div className="calc-field__header">
                <label>Peptide Amount</label>
                <span className="calc-value">{peptideAmount} mg</span>
              </div>
              <input type="range" min="1" max="20" step="0.5" value={peptideAmount} onChange={e => setPeptideAmount(Number(e.target.value))} className="calc-slider" />
              <div className="calc-range-labels"><span>1 mg</span><span>20 mg</span></div>
            </div>
            <div className="calc-field">
              <div className="calc-field__header">
                <label>Bacteriostatic Water Added</label>
                <span className="calc-value">{waterAmount} mL</span>
              </div>
              <input type="range" min="0.5" max="10" step="0.5" value={waterAmount} onChange={e => setWaterAmount(Number(e.target.value))} className="calc-slider" />
              <div className="calc-range-labels"><span>0.5 mL</span><span>10 mL</span></div>
            </div>
            <div className="calc-field">
              <div className="calc-field__header">
                <label>Desired Dose per Injection</label>
                <span className="calc-value">{desiredDose} mcg</span>
              </div>
              <input type="range" min="50" max="2000" step="25" value={desiredDose} onChange={e => setDesiredDose(Number(e.target.value))} className="calc-slider" />
              <div className="calc-range-labels"><span>50 mcg</span><span>2,000 mcg</span></div>
            </div>
          </div>

          {/* Results */}
          <div className="calc-results card">
            <h2>Calculation Results</h2>
            <div className="calc-result-item">
              <div className="calc-result-icon">⚗️</div>
              <div>
                <span>Concentration After Reconstitution</span>
                <strong>{concentration.toFixed(0)} mcg/mL</strong>
                <p>({(concentration / 1000).toFixed(3)} mg/mL)</p>
              </div>
            </div>
            <div className="calc-result-item highlight">
              <div className="calc-result-icon">💉</div>
              <div>
                <span>Volume Per Dose</span>
                <strong>{volumePerDose.toFixed(3)} mL</strong>
                <p>= {(volumePerDose * 100).toFixed(2)} units on a 100-unit syringe</p>
              </div>
            </div>
            <div className="calc-result-item">
              <div className="calc-result-icon">📦</div>
              <div>
                <span>Doses Per Vial</span>
                <strong>{isFinite(dosesPerVial) ? dosesPerVial.toFixed(0) : '—'} doses</strong>
                <p>at {desiredDose} mcg per dose</p>
              </div>
            </div>

            <div className="calc-disclaimer">
              ⚠️ For research planning only. Always verify with appropriate laboratory equipment. These calculations assume 100% solubility.
            </div>
          </div>
        </div>

        {/* Guide */}
        <div className="card calc-guide" style={{ marginTop: '2rem', padding: '2rem' }}>
          <h3>Reconstitution Guide</h3>
          <div className="grid-3" style={{ marginTop: '1.25rem' }}>
            {[['Step 1: Prepare','Allow vial to reach room temperature. Gather bacteriostatic water, insulin syringe, and alcohol wipes. Work in a clean environment.'],['Step 2: Add Water','Draw up the calculated volume of bacteriostatic water. Inject slowly down the side of the vial — never directly onto the powder. Do not shake.'],['Step 3: Dissolve','Gently swirl or roll the vial between your palms until fully dissolved. The solution should be clear. Store at 2-8°C.']].map(([title, desc]) => (
              <div key={title}>
                <h4 style={{ color: 'var(--green-600)', marginBottom: '0.5rem' }}>{title}</h4>
                <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', lineHeight: '1.7' }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
